import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { PestanasDeCuentas } from '../../cuentas/componentes/PestanasDeCuentas.jsx';
import { consultarMensualidades } from '../api/msiApi.js';
import { MarcaDeRecordatorio } from '../componentes/RecordatorioDePago.jsx';
import { importe as formatearImporte, LEYENDA_PROYECCION, LEYENDA_REPARTO_DE_DEUDA } from '../formato.js';

/**
 * Pestaña "Meses sin intereses" del destino Cuentas (RF-070, RF-077, D-62).
 *
 * EL 4/6 NO RECLAMA LUGAR EN LA BARRA INFERIOR y no desplaza a nadie al menú
 * secundario: vive como tercera pestaña junto a Cuentas y Tarjetas. La barra sigue
 * teniendo seis lugares y el sexto es de Ajustes (RN-044b, RF-042).
 *
 * QUÉ RESPONDE ESTA PANTALLA, y no es lo mismo que el historial (D-70, RN-121):
 * "CUÁNTO HAY QUE PAGAR ESTE MES". Solo mensualidades vigentes, de planes vigentes
 * y con la tarjeta activa. El "cuánto se gastó" lo responde el historial del 3/6,
 * que además incluye las liquidadas y no filtra por tarjeta activa.
 *
 * LO QUE ESTA PANTALLA NO HACE, y es regla y no estilo:
 *   · NO concatena el contador "1/12" ni divide importes: llegan ya armados del
 *     servidor (RNF-038).
 *   · NO suma estos totales a la deuda de la tarjeta: son un REPARTO de deuda ya
 *     contada, no deuda adicional (D-53, RN-103c).
 */
export default function VistaDelMes() {
  const hoy = obtenerFechaDeNegocioHoy();
  const periodoDeHoy = Number(`${hoy.slice(0, 4)}${hoy.slice(5, 7)}`);

  const [idPeriodo, setIdPeriodo] = useState(periodoDeHoy);
  const [agruparPor, setAgruparPor] = useState('CORTE');
  // Cuatro estados excluyentes (RF-087, RN-140 a RN-142): cambiar de mes o de
  // agrupación vuelve a CARGANDO, y un mes sin mensualidades sólo se declara vacío
  // cuando la respuesta llegó bien.
  const consultar = useCallback(
    () => consultarMensualidades({ idPeriodo, agruparPor, fechaLocal: hoy }),
    [idPeriodo, agruparPor, hoy],
  );
  const listado = useConsultaDeListado(
    consultar,
    (respuesta) => (respuesta?.mensualidadesDelMes ?? []).length === 0,
  );
  const { datos, estado, error } = listado;
  const cargar = listado.recargar;

  const renglones = datos?.mensualidadesDelMes ?? [];

  // EL SELECTOR NO CALCULA DÓNDE PARAR: el servidor informa el último periodo con
  // mensualidades y la pantalla lo obedece (D-73, RN-123d). Hacer que la persona
  // avance mes a mes hasta la nada es un defecto de diseño.
  const ultimo = datos?.ultimoPeriodoConMensualidades ?? null;
  const hayMasAdelante = ultimo === null ? false : idPeriodo < ultimo;

  return (
    <Shell titulo="Cuentas" subtitulo="Meses sin intereses">
      <PestanasDeCuentas activa="meses" />

      <ListadoActualizando visible={listado.actualizando} />

      {estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={error} alReintentar={cargar} />
      )}

      <div className="card" style={{ margin: 12 }} data-prueba="selector-de-mes">
        <div className="fila">
          <button
            className="btn sec"
            type="button"
            onClick={() => setIdPeriodo(mesAnterior(idPeriodo))}
          >
            Mes anterior
          </button>
          <span className="crece" style={{ textAlign: 'center' }}>
            <b>{etiquetaDePeriodo(idPeriodo)}</b>
          </span>
          <button
            className="btn sec"
            type="button"
            disabled={!hayMasAdelante}
            onClick={() => setIdPeriodo(mesSiguiente(idPeriodo))}
          >
            Mes siguiente
          </button>
        </div>

        {!hayMasAdelante && ultimo !== null && idPeriodo >= ultimo && (
          <span className="ayuda">No hay más mensualidades programadas.</span>
        )}

        <div className="campo">
          <label htmlFor="agruparPor">AGRUPAR POR</label>
          <select id="agruparPor" name="agruparPor" value={agruparPor}
            onChange={(evento) => setAgruparPor(evento.target.value)}>
            <option value="CORTE">Fecha de corte</option>
            <option value="LIMITE_PAGO">Fecha límite de pago</option>
          </select>
          <span className="ayuda">
            No son lo mismo: la fecha límite cae <b>en el mes siguiente</b> cuando el día de pago es
            menor o igual que el de corte.
          </span>
        </div>
      </div>

      {/* D-73 / RN-123c: UN MES FUTURO ES PROYECCIÓN, no gasto ocurrido, y se
          presenta con aspecto distinto al de un mes cerrado. LOS SALDOS Y LA DEUDA
          NUNCA SE PROYECTAN: son de hoy, siempre (RN-123g). */}
      {datos?.esPeriodoFuturo && (
        <div className="aviso i" data-prueba="aviso-de-proyeccion">
          <b>{LEYENDA_PROYECCION}.</b> Es un mes que todavía no ocurre: aquí solo hay mensualidades
          ya calculadas. <b>Tu saldo y tu deuda no se proyectan nunca</b>: los que ves en Cuentas son
          los de hoy.
        </div>
      )}

      {/* RN-142c: mientras carga no se pintan totales en cero. */}
      {(estado === ESTADO_DE_LISTADO.CARGANDO ? [] : (datos?.totalesPorMoneda ?? [])).map((total) => (
        <div key={total.codigoISO} className="card" style={{ margin: 12 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>{total.codigoISO}</h4>
          <div className="fila">
            <span className="crece">Cae en este mes</span>
            <b>{formatearImporte(total.totalImputadoAlMes, total.codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Falta por caer en cortes futuros</span>
            <b>{formatearImporte(total.totalPendiente, total.codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Ya cargado en cortes pasados</span>
            <b>{formatearImporte(total.totalCargado, total.codigoISO)}</b>
          </div>
          <span className="ayuda" data-prueba="leyenda-reparto-de-deuda">{LEYENDA_REPARTO_DE_DEUDA}</span>
        </div>
      ))}

      {(datos?.totalesPorTarjeta ?? []).length > 0 && (
        <div className="card lista" style={{ margin: 12 }} data-prueba="planes-por-tarjeta">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>POR TARJETA</h4>
          {datos.totalesPorTarjeta.map((tarjeta) => (
            <div className="fila" key={tarjeta.idCuenta}>
              <div className="crece">
                <b>{tarjeta.nombreTarjeta}</b>
                <MarcaDeRecordatorio visible={tarjeta.tieneRecordatorioDePago} />
                <small>{tarjeta.cantidadPlanesVigentes} plan(es) con mensualidades</small>
              </div>
              <span className="imp">
                {formatearImporte(tarjeta.totalImputadoAlMes, tarjeta.codigoISO)}
                <small>cae este mes</small>
              </span>
            </div>
          ))}
        </div>
      )}

      {estado === ESTADO_DE_LISTADO.CARGANDO && (
        <ListadoCargando renglones={4} texto="Cargando las mensualidades del mes…" />
      )}

      {estado === ESTADO_DE_LISTADO.VACIO ? (
        <div className="card" style={{ margin: 12 }}>
          <div style={{ textAlign: 'center', padding: '22px 16px' }}>
            <p><b>No tienes compras a meses en este mes.</b></p>
            <p>
              Una lista vacía <b>no es un error</b>: la consulta respondió correctamente con
              cero renglones y todos los totales en cero.
            </p>
          </div>
        </div>
      ) : estado === ESTADO_DE_LISTADO.CON_DATOS ? (
        <div className="card lista" style={{ margin: 12 }} data-prueba="mensualidades-del-mes">
          {renglones.map((renglon) => (
            <Link className="fila" key={renglon.idMensualidad} to={`/cuentas/meses/planes/${renglon.idPlan}`}>
              <div className="crece">
                <b>{renglon.descripcionDelGasto || 'Compra a meses'}</b>
                {/* CONTADOR YA ARMADO POR EL SERVIDOR: la pantalla no lo concatena
                    (D-65, RNF-038). */}
                <span className="chip" data-prueba="contador">{renglon.contadorDeMensualidad}</span>
                <small>
                  {renglon.nombreTarjeta} · {renglon.nombrePropietario} · corte {formatearFecha(renglon.fechaCorte)}
                </small>
              </div>
              <span className="imp">
                {/* LA CIFRA QUE SE MUESTRA POR MES es la mensualidad, no el total de
                    la compra (D-65, RN-117a). */}
                {formatearImporte(renglon.importeImputadoAlMes, renglon.codigoISO)}
                <small>de {formatearImporte(renglon.importeDelMovimiento, renglon.codigoISO)}</small>
              </span>
            </Link>
          ))}
        </div>
      ) : null}
    </Shell>
  );
}

/* Navegación del selector de mes. Es aritmética de PRESENTACIÓN sobre el AAAAMM
   que el servidor ya devolvió, no cálculo de calendario: las fechas de corte y de
   pago vienen resueltas del 2/6 y esta pantalla no las toca (D-56, RNF-035). */
function mesAnterior(idPeriodo) {
  const anio = Math.floor(idPeriodo / 100);
  const mes = idPeriodo % 100;
  return mes === 1 ? ((anio - 1) * 100) + 12 : (anio * 100) + mes - 1;
}

function mesSiguiente(idPeriodo) {
  const anio = Math.floor(idPeriodo / 100);
  const mes = idPeriodo % 100;
  return mes === 12 ? ((anio + 1) * 100) + 1 : (anio * 100) + mes + 1;
}

const NOMBRES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function etiquetaDePeriodo(idPeriodo) {
  const anio = Math.floor(idPeriodo / 100);
  const mes = idPeriodo % 100;
  return `${NOMBRES[mes - 1]} de ${anio}`;
}
